import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Plus } from "lucide-react";

const FAQS = [
  {
    q: "What is InvitationWala?",
    a: "InvitationWala is an Indian digital invitation design studio and online invitation maker. We create custom wedding invitations, birthday invitations, engagement, anniversary and party e-invites as videos, images and interactive cards — designed one-to-one on WhatsApp and delivered within 24 hours to families across India and worldwide.",
  },
  {
    q: "Do you make invitations in Hindi and other Indian languages?",
    a: "Yes — we design digital wedding cards and invites in Hindi, English and bilingual formats, along with Hindu, Muslim, Sikh and Christian wedding invitation styles featuring the right cultural motifs, shlokas and calligraphy for each ceremony.",
  },
  {
    q: "What are digital wedding invitations?",
    a: "Digital wedding invitations are custom-designed video, image or interactive cards sent to guests via WhatsApp, email, or Instagram instead of printed cards. At InvitationWala we craft cinematic invitation films, animated save-the-dates, and shareable suites for every wedding function — delivered in under 24 hours.",
  },
  {
    q: "How long does it take to create a custom invitation?",
    a: "Most custom digital invites are ready within 24 hours from the time we receive your wedding details and photos. Complex cinematic invitation films may take 48–72 hours. Express same-day delivery is available on request.",
  },
  {
    q: "How much do your wedding invitations cost?",
    a: "Pricing starts from very affordable rates for animated save-the-dates and scales up for cinematic invitation films, NRI multi-function suites and printed stationery. Share your function list on WhatsApp and we send a tailored quote in under an hour.",
  },
  {
    q: "Do you ship physical wedding stationery in India and abroad?",
    a: "Yes — our sister brand Stationery Wala delivers printed wedding stationery (welcome signs, itinerary cards, luggage tags, playing cards) across India and internationally for destination & NRI weddings.",
  },
  {
    q: "Can I customise the invite with my own photos and story?",
    a: "Absolutely — every InvitationWala design is built one-to-one on WhatsApp with your photos, story, fonts, colours and function timeline. No templates, no copy-paste.",
  },
  {
    q: "Which functions can you design invitations for?",
    a: "We design invites for every function — wedding day film, save the date reel, mehndi, haldi, sangeet, engagement / roka, reception, anniversary, birthday and corporate events.",
  },
  {
    q: "Can I send the digital invite to my whole family WhatsApp group?",
    a: "Yes — your finished invite is delivered as a high-quality MP4 video or image that can be forwarded freely on WhatsApp, posted to Instagram stories, or attached to an email broadcast.",
  },
];

export default function FAQSection() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-20 md:py-28 border-t border-white/5"
      style={{ background: "linear-gradient(180deg, #1a1414 0%, #161210 100%)" }}
    >
      {/* JSON-LD FAQPage schema for Google Rich Results + AI Overviews */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: f.a,
              },
            })),
          }),
        }}
      />

      <div className="max-w-[1100px] mx-auto px-5 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
            <span className="overline text-[var(--iw-gold)]">Frequently Asked</span>
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
          </div>
          <h2
            className="font-serif-display text-[var(--iw-ivory)] text-[36px] sm:text-5xl md:text-6xl leading-[0.95] tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Questions <span className="italic gold-rose-gradient">we love</span> to answer.
          </h2>
        </motion.div>

        <div className="space-y-2">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className="border-b border-white/10"
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full py-5 md:py-6 flex items-center justify-between gap-6 text-left group"
                  data-testid={`faq-toggle-${i}`}
                >
                  <h3
                    className="font-serif-display text-[var(--iw-ivory)] text-xl md:text-2xl leading-tight group-hover:text-[var(--iw-gold)] transition-colors"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {f.q}
                  </h3>
                  <span
                    className="shrink-0 w-9 h-9 grid place-items-center border transition-all"
                    style={{
                      borderColor: isOpen
                        ? "var(--iw-gold)"
                        : "rgba(255,255,255,0.15)",
                      background: isOpen
                        ? "rgba(212,175,55,0.12)"
                        : "transparent",
                      transform: isOpen ? "rotate(45deg)" : "rotate(0)",
                    }}
                  >
                    <Plus
                      size={14}
                      className={
                        isOpen ? "text-[var(--iw-gold)]" : "text-[var(--iw-faint)]"
                      }
                    />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p
                        className="text-[var(--iw-muted)] leading-relaxed pb-6 md:pb-7 pr-12 text-[15px] md:text-base"
                        data-testid={`faq-answer-${i}`}
                      >
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
